import { distinctUntilChanged, map, mapTo, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { EventsService } from '../../../services/events/events.service';
import { RepServices } from '../../../data-services/rep/rep.services';
import { SchedulerService } from '../../../services/scheduler/scheduler.service';
import { Scheduler } from "../../../classes/scheduler/scheduler.class";
import { DeviceService } from '../../../services/device/device.service';
import { Subject, merge, combineLatest, Observable, BehaviorSubject, of } from 'rxjs';
import { TrackService, TrackingEventNames } from '../../../services/logging/tracking.service';
import { ActivityService } from '../../../services/activity/activity.service';
import { REP_STATUS } from '../../../models/rep-status-model';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../services/alert/alert.service';
import { OrderByPipe } from '@omni/pipes/orderby.pipe';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { SchedulerDataService } from '@omni/data-services/scheduler/scheduler.data.service';
import { SharedModule } from '../../../shared/shared.module';
import { SchedulerItemComponent } from '../scheduler-item/scheduler-item';

@Component({
  selector: 'scheduler-list',
  templateUrl: 'scheduler-list.html',
  styleUrls: ['scheduler-list.scss'],
  standalone: true,
  imports: [SharedModule, SchedulerItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulerListComponent implements OnDestroy, OnInit {

  text: string;
  public isDeviceOnline = true;

  get searchText(): string {
    return this.searchTextChanged.value;
  }

  private readonly searchTextChanged = new BehaviorSubject<string>('');
  public schedulerList$: Observable<Scheduler[]>;
  private readonly nextSlice$ = new Subject<void>();
  public selectedSchedule: Scheduler;
  private readonly ngdestroy$ = new Subject<void>();
  private readonly sliceCount = 20;
  private readonly orderByPipe = new OrderByPipe();
  routesHeaderModel: IndSectionHeaderViewDataModel;

  private readonly deviceOnline$ = new Subject<boolean>();
  hasMoreData = false;

  constructor(
    public readonly events: EventsService,
    private readonly cd: ChangeDetectorRef,
    public readonly repService: RepServices,
    private readonly schedulerService: SchedulerService,
    readonly schedulerDataService: SchedulerDataService,
    public readonly deviceService: DeviceService,
    private readonly alertService: AlertService,
    private readonly trackingService: TrackService,
    private readonly activityService: ActivityService,
    private readonly translate: TranslateService,
  ) {}

  async ngOnInit() {
    this.isDeviceOnline = !this.deviceService.isOffline;

    this.schedulerService.schedulerObs
      .pipe(takeUntil(this.ngdestroy$))
      .subscribe(value => {
        this.selectedSchedule = value;
        this.cd.markForCheck();
      });

    this.schedulerList$ = merge(
      // initial DB load
      of(null).pipe(
        tap(() => this.schedulerDataService.loadSchedulerPatterns()),
        map(() => null)
      ),
      // reload DB when sync completes
      this.schedulerDataService.synched$.pipe(
        tap(() => this.schedulerDataService.loadSchedulerPatterns()),
        map(() => null)
      )
    ).pipe(
      // switch to scheduler list after each load
      switchMap(() =>
        combineLatest([
          this.schedulerService.schedulerListObservable,
          this.searchTextChanged.pipe(startWith(''))
        ])
      ),

      // filtering
      map(([schedulerList, searchText]) =>
        this.schedulerService.getFilteredSchedulesBySearchText(schedulerList, searchText)
      ),

      // ordering
      map(schedulerList =>
        this.orderByPipe.transform(schedulerList, "startdate", false)
      ),

      // slicing
      switchMap(schedulerList =>
        this.nextSlice$.pipe(
          startWith(null),
          map((_, pageIndex) => {
            const sliceEnd = Math.min((pageIndex + 1) * this.sliceCount, schedulerList.length);
            this.hasMoreData = sliceEnd < schedulerList.length;
            this.cd.markForCheck();
            return schedulerList.slice(0, sliceEnd);
          })
        )
      ),

      takeUntil(this.ngdestroy$)
    );

    combineLatest([
      this.translate.onLangChange.pipe(startWith(null)),
      merge(
        this.deviceService.isOfflineObservable.pipe(map(offline => !offline)),
        this.deviceOnline$
      ).pipe(
        startWith(!this.deviceService.isOffline),
        distinctUntilChanged()
      )
    ]).pipe(
      map(([_, online]) =>
        this.repService.getCurrentUserState() === REP_STATUS.OFFLINE.userState ? false : online
      ),
      distinctUntilChanged(),
      takeUntil(this.ngdestroy$)
    ).subscribe(isOnline => {
      this.isDeviceOnline = isOnline;
      this.getRoutesHeader();
      this.cd.markForCheck();
    });
  }

  @HostListener('window:omni:device:deviceIsOffline')
  deviceIsOffline() {
    this.deviceOnline$.next(false);
  }

  @HostListener('window:omni:device:deviceIsOnline')
  deviceIsOnline() {
    this.deviceOnline$.next(true);
  }

  ngOnDestroy() {
    this.schedulerService.setSchedule(undefined);
    this.activityService.selectedActivity = undefined;
    this.ngdestroy$.next();
    this.ngdestroy$.complete();
    this.nextSlice$.complete();
    this.searchTextChanged.complete();
    this.deviceOnline$.complete();
  }

  public getRoutesHeader() {
    const detailsHeaderControls = [
      {
        id: 'saved-routes-btn',
        text: this.translate.instant('SCHEDULE_NEW_PLAN'),
        isDisabled: !this.isDeviceOnline || this.deviceService.isOffline,
      }
    ];

    this.routesHeaderModel = {
      id: 'saved-routes-header',
      title: this.translate.instant('SAVED_ROUTES_PLANS'),
      controls: detailsHeaderControls,
    };
  }

  onSectionHeaderControlClick(id: string) {
    if (id === 'saved-routes-btn') {
      this.newScheduler();
    }
    this.getRoutesHeader();
  }

  async onSelect(event: Scheduler) {
    if (!this.schedulerService.areChangeSaved) {
      const res = await this.alertService.showAlert({
        title: this.translate.instant('SCHEDULER_LIST_ALERT_TITLE'),
        message: this.translate.instant('SCHEDULER_LIST_ALERT_MESSAGE')
      }, this.translate.instant('DISCARD'));

      if (res.role !== "ok") return;
      try {
        await this.schedulerService.discardCurrentChanges();
      } catch (err) {
        console.error(err);
      }
    }
    this.schedulerService.setSchedule(event);
  }

  newScheduler() {
    this.trackingService.tracking('SchedulerNewPattern', TrackingEventNames.SCHEDULER);

    if (this.deviceService.isOffline) return;

    const localSchedulePattern = this.schedulerService.getNewSchedule();
    localSchedulePattern.isNewScheduler = true;
    this.onSelect(localSchedulePattern);
  }

  doInfinite(event: any) {
    this.nextSlice$.next();
    event.target.complete();
  }

  private applySearch(searchText: string) {
    this.searchTextChanged.next(searchText);
  }

  onSearchInput(event: any) {
    this.applySearch(event.target.value);
  }

  trackByScheduleId(index: number, schedule: Scheduler): string {
    return schedule?.ID;
  }
}
